import { useCallback, useEffect, useRef, useState } from 'react';
import { SAMPLES, loadSample } from '../lib/samples';
const isImage = (f) => f.type.startsWith('image/');
/** Mac uses Cmd rather than Ctrl; showing the wrong one looks careless. */
const isMac = /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);
export default function DropZone({ onPick, disabled }) {
    const [dragging, setDragging] = useState(false);
    const [busySample, setBusySample] = useState(null);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);
    // Nested dragenter/dragleave events fire constantly; count them instead of
    // toggling, or the overlay flickers as the pointer crosses child elements.
    const dragDepth = useRef(0);
    const take = useCallback((files) => {
        const file = files && Array.from(files).find(isImage);
        if (file) {
            setError(null);
            onPick(file);
        }
        else if (files && files.length) {
            setError('That file is not an image. Try a PNG, JPG or WebP.');
        }
    }, [onPick]);
    // Accept a drop anywhere on the page, not just on the box. One less thing to
    // aim at, which matters more than it sounds for non-technical visitors.
    useEffect(() => {
        if (disabled)
            return;
        const onDragEnter = (e) => {
            if (!e.dataTransfer?.types.includes('Files'))
                return;
            dragDepth.current++;
            setDragging(true);
        };
        const onDragOver = (e) => e.preventDefault();
        const onDragLeave = () => {
            dragDepth.current = Math.max(0, dragDepth.current - 1);
            if (dragDepth.current === 0)
                setDragging(false);
        };
        const onDrop = (e) => {
            e.preventDefault();
            dragDepth.current = 0;
            setDragging(false);
            take(e.dataTransfer?.files);
        };
        const onPaste = (e) => {
            const file = Array.from(e.clipboardData?.files ?? []).find(isImage);
            if (file) {
                e.preventDefault();
                onPick(file);
            }
        };
        window.addEventListener('dragenter', onDragEnter);
        window.addEventListener('dragover', onDragOver);
        window.addEventListener('dragleave', onDragLeave);
        window.addEventListener('drop', onDrop);
        window.addEventListener('paste', onPaste);
        return () => {
            window.removeEventListener('dragenter', onDragEnter);
            window.removeEventListener('dragover', onDragOver);
            window.removeEventListener('dragleave', onDragLeave);
            window.removeEventListener('drop', onDrop);
            window.removeEventListener('paste', onPaste);
        };
    }, [take, onPick, disabled]);
    const pickSample = async (sample) => {
        if (disabled)
            return;
        setBusySample(sample.id);
        setError(null);
        try {
            onPick(await loadSample(sample));
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load that sample');
        }
        finally {
            setBusySample(null);
        }
    };
    return (<>
      {dragging && (<div className="dropveil">
          <div className="dropveil-inner">Drop your image to start</div>
        </div>)}

      <div className={`uploader${dragging ? ' over' : ''}`}>
        <button className="big-cta" disabled={disabled} onClick={() => inputRef.current?.click()}>
          Upload image
        </button>

        <p className="uploader-hint">
          or drop a file anywhere
          {/* Hidden on touch/small screens, where neither applies. */}
          <span className="paste-hint">
            {' '}
            &middot; paste with <kbd>{isMac ? '⌘' : 'Ctrl'}</kbd>+<kbd>V</kbd>
          </span>
        </p>

        <div className="samples">
          <span className="samples-label">No image handy? Try one:</span>
          <div className="samples-row">
            {SAMPLES.map((s) => (<button key={s.id} className="sample" disabled={disabled || busySample !== null} onClick={() => void pickSample(s)} title={`Try the ${s.label.toLowerCase()} sample`}>
                <img src={s.src} alt="" loading="lazy"/>
                <span>{busySample === s.id ? 'Loading…' : s.label}</span>
              </button>))}
          </div>
        </div>

        {error && <p className="warn uploader-error">{error}</p>}

        <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => {
            take(e.target.files);
            e.target.value = ''; // allow re-picking the same file
        }}/>
      </div>
    </>);
}
